import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean; // botón de confirmar en rojo (acciones destructivas)
}

const ConfirmContext = createContext<(o: ConfirmOptions) => Promise<boolean>>(async () => false);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | undefined>(undefined);
  useBodyScrollLock(!!state);

  const confirm = useCallback((o: ConfirmOptions) => new Promise<boolean>((res) => {
    resolver.current = res;
    setState(o);
  }), []);

  const close = (v: boolean) => {
    setState(null);
    const r = resolver.current;
    resolver.current = undefined;
    r?.(v);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div onClick={() => close(false)} role="dialog" aria-modal="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 60000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, animation: 'cfFade .15s ease' }}>
          <style>{`@keyframes cfFade{from{opacity:0}to{opacity:1}}@keyframes cfPop{from{transform:scale(.96);opacity:.5}to{transform:scale(1);opacity:1}}`}</style>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.3)', overflow: 'hidden', animation: 'cfPop .18s cubic-bezier(.16,1,.3,1)' }}>
            <div style={{ padding: '18px 20px 6px' }}>
              {state.title && <div style={{ fontWeight: 800, fontSize: 17, color: '#0f172a', marginBottom: 6 }}>{state.title}</div>}
              <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.5 }}>{state.message}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px 18px' }}>
              <button onClick={() => close(false)}
                style={{ border: '1px solid #e2e8f0', background: '#fff', color: '#475569', borderRadius: 10, padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {state.cancelText || 'Cancelar'}
              </button>
              <button onClick={() => close(true)} autoFocus
                style={{ border: 'none', background: state.danger ? '#dc2626' : '#2563eb', color: '#fff', borderRadius: 10, padding: '9px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                {state.confirmText || 'Aceptar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => useContext(ConfirmContext);
